import { I18nService } from '../utils/i18n.service'
import { openableIntersection, openableOverMidnight, openableUnserialize } from '../utils/openables.service'
import type { UnserializedMultiOpeningHours } from '../utils/openables.service'
import { formatTime, parseTimeRange } from '../utils/utils.service'
import type { WorkingDay } from './offered-services.service'
import i18nScopedData from './periods.service.yml'

const i18nScoped = new I18nService(i18nScopedData)

export const periodPresets = [
  { name: 'morning', from_hour: '06:00', until_hour: '12:00' },
  { name: 'early_afternoon', from_hour: '12:00', until_hour: '15:00' },
  { name: 'afternoon', from_hour: '15:00', until_hour: '18:00' },
  { name: 'late_afternoon', from_hour: '18:00', until_hour: '21:00' },
  { name: 'night', from_hour: '21:00', until_hour: '30:00' },
]

export function periodI18nName (name: string) {
  return i18nScoped.t(name)
}

export function getPeriodsForOpenable<T extends UnserializedMultiOpeningHours> (multiOpeningHours: T) {
  const allWorkingDays = [1, 2, 3, 4, 5, 6, 7] as WorkingDay[]

  /** Convert period presets to an openable
   * A openablePeriodPresets is opened 7 days a week to support intersection with openable over midnight.
   */
  const openablePeriodPresets = openableUnserialize({
    working_days: allWorkingDays,
    multi_opening_hours: Object.fromEntries(
      allWorkingDays.map(workingDay => [
        workingDay,
        periodPresets.map(periodPreset => [periodPreset.from_hour, periodPreset.until_hour])
      ])
    )
  })
  
  const [midnightSafeOpenablePeriodPresets, midnightSafeMultiOpeningHours] = [openablePeriodPresets, multiOpeningHours].map(openableOverMidnight)
  
  const rawWorkingDayPeriods = openableIntersection([midnightSafeOpenablePeriodPresets, midnightSafeMultiOpeningHours])
  
  return Object.fromEntries(
    Object.entries(rawWorkingDayPeriods).map(([day, rawPeriods]) => {
      /**
       * Because rawWorkingDayPeriods is midnightSafe, this object has an additional midnightSafe property of type boolean.
       * 
       * We ignore this value to only return periods with from_hour and until_hour adjusted with the intersection
       * of openablePeriodPresets and multiOpeningHours.
       */
      if (typeof rawPeriods === 'boolean') return null

      return [
        day,
        rawPeriods.map(rawPeriod => {
          const period = periodPresets.find(preset => rawPeriod.every(time => {
            const [fromHour, untilHour] = parseTimeRange(preset.from_hour, preset.until_hour)
            return fromHour <= time && time <= untilHour
          }))
          
          return {  ...period, from_hour: formatTime(rawPeriod[0]), until_hour: formatTime(rawPeriod[1]) }
        })
      ]
    })
  )
}
